import { motion } from 'motion/react';
import { Users, Hash, Compass, MessageCircle, User, Bell } from 'lucide-react';
import { useNotificationBadges } from './NotificationContext';
import { useUserData } from './UserDataContext';
import { useMessaging } from './MessagingContext';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { UnreadBadge } from './ui/UnreadBadge';

interface BottomNavProps {
  currentScreen: string;
  onNavigate: (screen: string) => void;
  onNotifications: () => void;
}

const navItems = [
  { id: 'discover', label: 'Discover', icon: Users },
  { id: 'yaptab', label: 'YapTab', icon: Hash },
  { id: 'explore', label: 'Explore', icon: Compass },
  { id: 'chat', label: 'Chat', icon: MessageCircle },
  { id: 'profile', label: 'Profile', icon: User },
];

export function BottomNav({ currentScreen, onNavigate, onNotifications }: BottomNavProps) {
  const { unreadCount, incomingRequestCount } = useNotificationBadges();
  const { userProfile } = useUserData();
  const { totalUnread } = useMessaging();

  return (
    <div 
      className="bg-light-pattern dark:bg-[#000000] border-t border-black/10 dark:border-white/10 backdrop-blur-lg"
      style={{ paddingBottom: 'calc(max(env(safe-area-inset-bottom), 12px))' }}
    >
      <div className="w-full max-w-2xl mx-auto px-2 pt-3">


        {/* Navigation Items */}
        <div className="flex items-center justify-between">
          {navItems.map((item) => {
            const isActive = currentScreen === item.id;
            return (
              <motion.button
                key={item.id}
                whileTap={{ scale: 0.9 }}
                onClick={() => onNavigate(item.id)}
                className="flex flex-col items-center gap-1 px-4 py-2 relative"
              >
                {isActive && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute inset-0 bg-linear-to-r from-[#E8543B]/20 to-[#E74C3C]/20 dark:from-[#60a5fa]/20 dark:to-[#3b82f6]/20 rounded-xl"
                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                  />
                )}
                {item.id === 'profile' && userProfile.portfolio && userProfile.portfolio.length > 0 ? (
                  <div className="relative z-10">
                    <Avatar className={`w-6 h-6 border-2 ${isActive ? 'border-[#E8543B] dark:border-[#60a5fa]' : 'border-transparent'}`}>
                      <AvatarImage src={userProfile.portfolio[userProfile.profilePictureIndex || 0]} className="object-cover" />
                      <AvatarFallback className="text-[8px]">{userProfile.name?.[0] || 'U'}</AvatarFallback>
                    </Avatar>
                  </div>
                ) : (
                  <div className="relative z-10">
                    <item.icon
                      className={`w-6 h-6 ${isActive
                        ? 'text-[#E8543B] dark:text-[#60a5fa]'
                        : 'text-[#333333] dark:text-[#E5E5E5]'
                        }`}
                    />
                    {item.id === 'discover' && (
                      <UnreadBadge count={incomingRequestCount} style={{ top: -8, right: -8 }} />
                    )}
                    {item.id === 'chat' && (
                      <UnreadBadge count={totalUnread} style={{ top: -8, right: -8 }} />
                    )}
                  </div>
                )}
                <span
                  className={`text-xs relative z-10 ${isActive
                    ? 'text-[#E8543B] dark:text-[#60a5fa]'
                    : 'text-[#333333] dark:text-[#E5E5E5]'
                    }`}
                >
                  {item.label}
                </span>
              </motion.button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
